/*
 * Copyright (c) 2022, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React from 'react'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {setInitialScriptState} from '../../contexts/script-context'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {getSiteEnvVarValue} from '../../utils/site-config'
import {generateAlternateUrls} from '../../utils/utils'
import {renderLayoutsSwitch} from '../../components/optimizely/utils/contentRenderer'
import {HTTPError} from '@salesforce/pwa-kit-react-sdk/ssr/universal/errors'

// Components
import {
    Box,
    Flex,
    Grid,
    Select,
    Button,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {HideOnDesktop, HideOnMobile} from '@salesforce/retail-react-app/app/components/responsive'
import Seo from '../../components/seo'
import Optimizely from '../../components/optimizely'
import {GDIDynamicContentProvider} from '../../components/optimizely/gdi/utils/gdi-dynamic-content-context'

// Partials
import AbovePageHeader from './partials/above-page-header'
import PageHeader from './partials/page-header'
import PageFilters from './partials/page-filters'
import SelectedRefinements from './partials/selected-refinements'
import EmptySearchResults from './partials/empty-results'
import EmptySearchResultsTitle from './partials/empty-results-title'
import LowResultsCarousel from './partials/low-results-carousel'
import ProductGrid from './partials/product-grid'

// Constants
import {DEFAULT_LIMIT_VALUES} from '@salesforce/retail-react-app/app/constants'

// Error page
import PageNotFound from '../page-not-found'

// Page hook - all state, queries, effects and action handlers
import {useProductList} from './use-product-list'

/*
 * This is a simple product listing page. It displays a paginated list
 * of product hit objects. Allowing for sorting and filtering based on the
 * allowable filters and sort refinements.
 */
const ProductList = (props) => {
    // eslint-disable-next-line react/prop-types, @typescript-eslint/no-unused-vars
    const {isLoading: _unusedIsLoading, staticContext, ...rest} = props
    const {formatMessage} = useIntl()
    const pageFiltersStyles = useMultiStyleConfig('PageFilters')
    const {app: appConfig} = getConfig()
    const {site} = useMultiSite()
    const siteId = site?.id
    if (getSiteEnvVarValue(appConfig, 'IS_NOIBU_DISABLED_PLP', siteId) === 'true') {
        setInitialScriptState('noibu', false)
    }

    const {
        // State
        sortOpen, setSortOpen, filtersLoading, gridView, setGridViewPersisted,
        skeletonPlaceholders, allProducts, isCategoryChanging, page,
        minPageLoaded, maxPageLoaded, isLoadingNewPage, showLoadPrevious,
        // Search
        isLoading, isFetched, isRefetching, productSearchResult,
        searchQuery, isSearch, searchParams,
        // Category / CMS
        category,
        clpPage, isClpMode, isClpLoading,
        searchNoResultsPage, isSearchNoResultsLoading, hasSearchNoResultsPage,
        effectiveCategoryId, hasSEOUrlParams, serverSeoNotFound, clientSeoNotFound,
        // Grid
        isAlternativeGridView, gridColumns, currentBreakpoint, isDesktopBreakpoint,
        desktopGroupSize, gridInterruptorPositions,
        // CMS sections (PLP)
        aboveProductListBlocks, belowProductListBlocks,
        // Derived
        error, showNoResults, hasSelectedFilters, total, sortingOptions,
        sortUrls, limitUrls, basePath,
        pageTitle, limit, getLimitForPage, loadedLimitTotal,
        // Handlers
        toggleFilter, resetFilters, goToPage, loadPrevious, handleProductClick,
        addItemToWishlist, removeItemFromWishlist,
        // Enrichment / tracking
        enrichProduct, observeProduct, productRefs,
        prependScrollAnchor,
        // Wishlist
        wishlist,
        // Navigation
        history, location, onOpen
    } = useProductList()

    /**************** Error Handling ****************/
    const shouldRenderSeoNotFound =
        hasSEOUrlParams && !effectiveCategoryId && (serverSeoNotFound || clientSeoNotFound)

    if (shouldRenderSeoNotFound) return <PageNotFound />

    const hideFiltersAndSort = category?.c_HideFiltersAndSort === true

    const errorStatus = error?.response?.status
    switch (errorStatus) {
        case undefined:
            break
        case 404:
            return <PageNotFound />
        default:
            throw new HTTPError(errorStatus, `HTTP Error ${errorStatus} occurred.`)
    }

    // CLP mode: Optimizely has authored a page keyed by this category id —
    // render it as a full CMS replacement (no grid, filters, or PLP chrome).
    if (isClpMode) {
        return (
            <Box data-testid="sf-product-list-page" data-clp="true" {...rest}>
                <Optimizely content={clpPage} isLoading={isClpLoading} />
            </Box>
        )
    }

    // Search no-results CMS page: Optimizely has authored a page with SimpleAddress
    // 'search-no-results' — render it as a full page replacement with no PLP chrome.
    if (showNoResults && isSearch && hasSearchNoResultsPage) {
        return (
            <Box data-testid="sf-product-list-page" data-search-no-results="true" {...rest}>
                <GDIDynamicContentProvider value={{searchTerm: searchQuery}}>
                    <Optimizely content={searchNoResultsPage} isLoading={isSearchNoResultsLoading} />
                </GDIDynamicContentProvider>
            </Box>
        )
    }

    /**************** Selected Filters ****************/
    const renderDesktopSelectedFilters = () => {
        if (!hasSelectedFilters) return null
        return (
            <HideOnMobile>
                <Box {...pageFiltersStyles.srPlpContainer}>
                    <SelectedRefinements
                        filters={productSearchResult?.refinements?.filter((r) =>
                            r?.values?.some((v) => v.hitCount > 0)
                        )}
                        toggleFilter={toggleFilter}
                        handleReset={resetFilters}
                        selectedFilterValues={productSearchResult?.selectedRefinements}
                    />
                </Box>
            </HideOnMobile>
        )
    }

    const renderMobileSelectedFilters = () => {
        if (!hasSelectedFilters) return null
        return (
            <HideOnDesktop>
                <Box {...pageFiltersStyles.srPlpContainer}>
                    <SelectedRefinements
                        filters={productSearchResult?.refinements}
                        toggleFilter={toggleFilter}
                        handleReset={resetFilters}
                        selectedFilterValues={productSearchResult?.selectedRefinements}
                    />
                </Box>
            </HideOnDesktop>
        )
    }

    /**************** SEO meta tags ****************/
    let hasDescriptionMetaTag = false
    let hasKeywordsTag = false
    return (
        <Box
            className="sf-product-list-page"
            data-testid="sf-product-list-page"
            layerStyle="PLPpage"
            {...rest}
        >
            <Seo
                title={pageTitle || (category?.pageTitle ?? searchQuery)}
                noIndex={isSearch}
                canonicalUrl={category?.c_canonicalURL}
            >
                {productSearchResult?.pageMetaTags?.map((tag) => {
                    if (tag.id !== 'title') {
                        if (tag.id === 'description') hasDescriptionMetaTag = true
                        if (tag.id === 'keywords') hasKeywordsTag = true
                        return (
                            <meta
                                key={tag.id}
                                {...(tag.id.startsWith('og:')
                                    ? {property: tag.id, content: tag.value}
                                    : {name: tag.id, content: tag.value})}
                            />
                        )
                    }
                })}
                {!hasDescriptionMetaTag && (
                    <meta name="description" content={category?.pageDescription ?? searchQuery} />
                )}
                {!hasKeywordsTag && <meta name="keywords" content={category?.pageKeywords} />}
                {category?.c_canonicalURL && (
                    <link rel="canonical" href={category.c_canonicalURL} />
                )}
                {generateAlternateUrls(category?.c_alternateURLs)}
            </Seo>

            {!showNoResults && (
                <>
                    <AbovePageHeader />
                    <PageHeader
                        searchQuery={searchQuery}
                        category={category}
                        productSearchResult={productSearchResult}
                    />
                    {aboveProductListBlocks?.length > 0 && (
                        <Box my={{base: 6, lg: 8}}>
                            {aboveProductListBlocks.map((block, index) => (
                                <Box key={`above-plp-${block?.ContentLink?.GuidValue ?? index}`}>
                                    {renderLayoutsSwitch(block)}
                                </Box>
                            ))}
                        </Box>
                    )}
                </>
            )}

            {showNoResults && (
                <EmptySearchResultsTitle
                    hasSelectedFilters={
                        productSearchResult?.selectedRefinements
                            ? Object.keys(productSearchResult.selectedRefinements).filter(
                                  (key) => key !== 'q' && key !== 'cgid'
                              ).length > 0
                            : false
                    }
                />
            )}

            {(!showNoResults || hasSelectedFilters) && (
                <PageFilters
                    category={category}
                    productSearchResult={productSearchResult}
                    isLoading={isLoading}
                    toggleFilter={toggleFilter}
                    searchParams={searchParams}
                    resetFilters={resetFilters}
                    onSortClick={() => setSortOpen(true)}
                    onFiltersClick={() => onOpen()}
                    isVisible
                    gridView={gridView}
                    setGridView={setGridViewPersisted}
                    hideFiltersAndSort={hideFiltersAndSort}
                    hideSortAndGrid={showNoResults && hasSelectedFilters}
                />
            )}

            {!hideFiltersAndSort && showNoResults && hasSelectedFilters && (
                <Box>
                    {renderDesktopSelectedFilters()}
                    {renderMobileSelectedFilters()}
                </Box>
            )}

            {showNoResults ? (
                <EmptySearchResults />
            ) : (
                <>
                    {/* Visible when earlier pages exist before the current group (group start
                        > 1). A page-2 landing (group start 1) stays hidden; a page-4 landing
                        (group start 3) shows it even while page 3 loads. loadPrevious does a
                        scroll-preserving prepend, so the content stays put and the button
                        scrolls away as pages load above. See showLoadPrevious in the hook. */}
                    {showLoadPrevious && (
                        <Flex justifyContent="center" gap={4} my={7}>
                            <Button
                                variant="webPrimary"
                                onClick={loadPrevious}
                                width="100%"
                                maxWidth={{base: '100%', lg: '342px'}}
                            >
                                {formatMessage({
                                    id: 'plp.button.show.previous',
                                    defaultMessage: 'Show Previous'
                                })}
                            </Button>
                        </Flex>
                    )}

                    {!hideFiltersAndSort && renderDesktopSelectedFilters()}
                    {!hideFiltersAndSort && renderMobileSelectedFilters()}

                    <Grid templateColumns={{base: '1fr', md: '1fr'}} columnGap={6}>
                        <ProductGrid
                            allProducts={allProducts}
                            productSearchResult={productSearchResult}
                            skeletonPlaceholders={skeletonPlaceholders}
                            isRefetching={isRefetching}
                            isFetched={isFetched}
                            isCategoryChanging={isCategoryChanging}
                            searchParams={searchParams}
                            isAlternativeGridView={isAlternativeGridView}
                            gridColumns={gridColumns}
                            currentBreakpoint={currentBreakpoint}
                            isDesktopBreakpoint={isDesktopBreakpoint}
                            desktopGroupSize={desktopGroupSize}
                            gridInterruptorPositions={gridInterruptorPositions}
                            enrichProduct={enrichProduct}
                            observeProduct={observeProduct}
                            productRefs={productRefs}
                            prependScrollAnchor={prependScrollAnchor}
                            wishlist={wishlist}
                            addItemToWishlist={addItemToWishlist}
                            removeItemFromWishlist={removeItemFromWishlist}
                            handleProductClick={handleProductClick}
                            total={total}
                            page={page}
                            minPageLoaded={minPageLoaded}
                            maxPageLoaded={maxPageLoaded}
                            goToPage={goToPage}
                            isLoadingNewPage={isLoadingNewPage}
                            isSearch={isSearch}
                            getLimitForPage={getLimitForPage}
                            loadedLimitTotal={loadedLimitTotal}
                        />

                        {/* Hidden page-size select - kept for potential future use */}
                        <Flex
                            display="none"
                            justifyContent={['center', 'center', 'flex-start']}
                            paddingTop={8}
                        >
                            <Select
                                value={basePath}
                                onChange={({target}) => history.push(target.value)}
                            >
                                {limitUrls.map((href, index) => (
                                    <option key={href} value={href}>
                                        {DEFAULT_LIMIT_VALUES[index]}
                                    </option>
                                ))}
                            </Select>
                        </Flex>
                    </Grid>

                    {!isLoading && total !== undefined && total <= 4 && (
                        <LowResultsCarousel />
                    )}

                    {belowProductListBlocks?.length > 0 && (
                        <Box marginTop={{base: 8, lg: 12}}>
                            {belowProductListBlocks.map((block, index) => (
                                <Box key={`below-plp-${block?.ContentLink?.GuidValue ?? index}`}>
                                    {renderLayoutsSwitch(block)}
                                </Box>
                            ))}
                        </Box>
                    )}
                </>
            )}
        </Box>
    )
}

ProductList.getTemplateName = () => 'product-list'

ProductList.propTypes = {
    onAddToWishlistClick: PropTypes.func,
    onRemoveWishlistClick: PropTypes.func,
    category: PropTypes.object
}

// PWA Kit getProps method to pass server-side data to the component

export default ProductList
